import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { map, switchMap, take, combineLatest, Observable, Subscription } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  ControlData,
  FinnairAdvancePassengerInformationRequestV2,
  FinnairApisRequirementEmergencyContact,
  FinnairApisRequirementKeyEnum,
  FinnairEmergencyContactData,
  FinnairOrder,
  FinnairPhoneType,
} from '@fcom/dapi/api/models';
import { SelectOption } from '@fcom/common/interfaces';
import { ButtonType } from '@fcom/ui-components/components/buttons';
import { NAME_PATTERN, PHONE_PATTERN, PHONE_PREFIX_PATTERN, unsubscribe } from '@fcom/core/utils';
import { PhonePrefixService } from '@fcom/common/services';
import { NotificationTheme } from '@fcom/ui-components/components/notifications';

import { FinnairPassengerItemWithApis } from '../../../check-in/interface/check-in.interface';
import { ManageBookingService } from '../../../../services';

@Component({
  selector: 'fin-apis-emergency-contact',
  templateUrl: 'apis-emergency-contact.component.html',
  styleUrls: ['./../apis-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ApisEmergencyContactComponent implements OnInit, OnDestroy {
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonType = ButtonType;

  @Input()
  order$: Observable<FinnairOrder>;
  @Input()
  boundId$: Observable<string>;
  @Input()
  passenger$: Observable<FinnairPassengerItemWithApis>;
  @Input()
  controlData: ControlData;

  @Output() completed = new EventEmitter<void>();

  phonePrefixes$: Observable<SelectOption[]>;

  emergencyContactForm: FormGroup<{
    contactName: FormControl<string>;
    phonePrefix: FormControl<string>;
    phoneNumber: FormControl<string>;
  }>;

  loading = false;

  private subscriptions = new Subscription();

  constructor(
    private phonePrefixService: PhonePrefixService,
    private manageBookingService: ManageBookingService
  ) {}

  ngOnInit(): void {
    this.phonePrefixes$ = this.phonePrefixService.setupPhonePrefixes(false).pipe(take(1));
    this.subscriptions.add(
      this.passenger$.pipe(take(1)).subscribe((passenger) => {
        const emergencyContactData: FinnairApisRequirementEmergencyContact = passenger.apisCpr?.requirements?.find(
          (requirement) => requirement.key === FinnairApisRequirementKeyEnum.EMERGENCY_CONTACT
        )?.data as FinnairApisRequirementEmergencyContact;
        this.emergencyContactForm = new FormGroup({
          contactName: new FormControl(emergencyContactData?.contactName, [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(20),
            Validators.pattern(NAME_PATTERN),
          ]),
          phonePrefix: new FormControl(
            emergencyContactData?.phoneNumber?.countryCode && emergencyContactData?.phoneNumber?.countryPrefix
              ? `${emergencyContactData?.phoneNumber?.countryCode}|${emergencyContactData?.phoneNumber?.countryPrefix}`
              : '',
            [Validators.required, Validators.pattern(PHONE_PREFIX_PATTERN)]
          ),
          phoneNumber: new FormControl(emergencyContactData?.phoneNumber?.number ?? '', [
            Validators.required,
            Validators.pattern(PHONE_PATTERN),
          ]),
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  saveAndContinue(): void {
    if (this.emergencyContactForm.dirty && this.emergencyContactForm.valid) {
      this.emergencyContactForm.disable();
      this.loading = true;
      this.subscriptions.add(
        this.createEmergencyContactUpdateRequest()
          .pipe(
            take(1),
            switchMap((request) => this.manageBookingService.updateRegulatoryDetails(request))
          )
          .subscribe({
            next: () => {
              this.emergencyContactForm.enable();
              this.loading = false;
              this.completed.emit();
            },
            error: () => {
              this.emergencyContactForm.enable();
              this.loading = false;
            },
          })
      );
    } else if (this.emergencyContactForm.valid) {
      this.completed.emit();
    } else {
      this.markFormAsTouched();
    }
  }

  private markFormAsTouched(): void {
    Object.keys(this.emergencyContactForm.controls).forEach((field) => {
      const control = this.emergencyContactForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }
  private createEmergencyContactUpdateRequest(): Observable<FinnairAdvancePassengerInformationRequestV2[]> {
    return combineLatest([this.boundId$, this.passenger$]).pipe(
      map(([boundId, passenger]) => {
        const phonePrefixValues = this.emergencyContactForm.controls.phonePrefix.value.split('|');
        return [
          {
            advancePassengerInformation: [
              {
                emergencyContact: {
                  contactName: this.emergencyContactForm.controls.contactName.value,
                  phoneNumber: {
                    countryCode: phonePrefixValues[0],
                    countryPrefix: phonePrefixValues[1],
                    number: this.emergencyContactForm.controls.phoneNumber.value,
                    type: FinnairPhoneType.EMERGENCY,
                  },
                } as FinnairEmergencyContactData,
                travelerId: passenger.id,
              },
            ],
            boundId,
          },
        ];
      }),
      take(1)
    );
  }

  protected readonly NotificationTheme = NotificationTheme;
}
